/**
 * AnteprimaDestinatari — «questo contenuto raggiungerà N persone su M aziende».
 *
 * PERCHÉ ESISTE: senza un numero davanti agli occhi si finisce per annunciare
 * tutto a tutti per pigrizia. Il numero non è decorativo, è il freno.
 *
 * Il conteggio passa da `contaDestinatari` (`shared/kb/kbAudience`) — lo STESSO
 * filtro che userà il widget in home dei lettori. Se la console contasse a modo
 * suo, l'editore pubblicherebbe per un pubblico e ne raggiungerebbe un altro.
 *
 * La PLATEA (utenti + aziende) arriva come prop: la pagina la legge una volta
 * sola e la passa in giù. Farla leggere qui significherebbe rileggere
 * `app_users` a ogni apertura del dialogo, e due volte quando anche l'editor ne
 * ha bisogno per la tendina dei ruoli e dell'owner.
 *
 * Doc canonico: docs/01-ARCHITECTURE/KB-CONTENT-MODEL.md
 */

import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Collapse, Paper, Stack, Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore, GroupsOutlined } from '@mui/icons-material';
import { tokens } from '../../theme';
import { SoftChip } from '../SoftChip';
import { calcolaAnteprimaDestinatari } from '../../services/kbContentsService';
import type { KbPlatea } from '../../types/kb';
import type { KbContenuto } from '@shared/kb/kbTypes';

interface Props {
  /** Il contenuto COME SARÀ salvato: audience e targeting già applicati. */
  contenuto: KbContenuto;
  /** Utenti e aziende, letti una volta dalla pagina. `null` finché carica. */
  platea: KbPlatea | null;
  /** Errore di lettura della platea: si dice, non si mostra uno zero finto. */
  errorePlatea?: string | null;
}

/** Altezza minima dei bersagli touch: la console si usa anche da tablet. */
const TOUCH_MIN = 40;

export const AnteprimaDestinatari: React.FC<Props> = ({ contenuto, platea, errorePlatea }) => {
  const [dettaglioAperto, setDettaglioAperto] = useState(false);

  /**
   * ⚠️ Si valuta il contenuto COME SE FOSSE PUBBLICATO.
   *
   * `filtraVisibili` scarta tutto ciò che non è `pubblicato` — giustamente, è la
   * regola del lettore. Ma la domanda dell'editore è un'altra: «se lo pubblico
   * adesso, chi lo riceve?». Passando lo stato reale, l'anteprima direbbe
   * «raggiunge nessuno» su ogni bozza, cioè su ogni contenuto mentre lo si
   * scrive: un numero sempre zero è un numero che si impara a ignorare.
   */
  const comeSePubblicato = useMemo<KbContenuto>(
    () => ({ ...contenuto, stato: 'pubblicato' }),
    [contenuto],
  );

  const anteprima = useMemo(
    () => (platea ? calcolaAnteprimaDestinatari(comeSePubblicato, platea) : null),
    [comeSePubblicato, platea],
  );

  const nomiAzienda = useMemo(() => {
    const mappa = new Map<string, string>();
    for (const t of platea?.tenants ?? []) mappa.set(t.id, t.nome_azienda || t.id);
    return mappa;
  }, [platea]);

  const righeDettaglio = useMemo(() => {
    if (!anteprima) return [];
    return Object.entries(anteprima.per_tenant)
      .map(([tenantId, persone]) => ({
        tenantId,
        nome: nomiAzienda.get(tenantId) ?? (tenantId === 'senza_azienda' ? 'senza azienda' : tenantId),
        persone,
      }))
      .sort((a, b) => b.persone - a.persone || a.nome.localeCompare(b.nome, 'it'));
  }, [anteprima, nomiAzienda]);

  // ── Errore di lettura: «non ho potuto contare» non è «raggiunge zero persone» ──
  if (errorePlatea) {
    return (
      <Alert severity="error" sx={{ mb: 0 }}>
        Non è stato possibile calcolare i destinatari: {errorePlatea}
        {' '}Il contenuto si può salvare, ma pubblicarlo senza sapere chi lo riceve
        non è una buona idea.
      </Alert>
    );
  }

  if (!platea || !anteprima) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 2, borderColor: tokens.color.line, bgcolor: tokens.color.bone }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2" sx={{ color: tokens.color.ash }}>
            Conto i destinatari…
          </Typography>
        </Stack>
      </Paper>
    );
  }

  const nessuno = anteprima.totale === 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderColor: nessuno ? tokens.color.warning : tokens.color.line,
        bgcolor: tokens.color.bone,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <GroupsOutlined sx={{ color: tokens.color.ash, mt: 0.25 }} fontSize="small" />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontFamily: tokens.font.display,
              fontWeight: 600,
              fontSize: '1.05rem',
              color: nessuno ? tokens.color.warning : tokens.color.graphite,
              lineHeight: 1.3,
            }}
          >
            {nessuno
              ? 'Così com’è, non raggiunge nessuno'
              : `Raggiunge ${anteprima.totale} ${anteprima.totale === 1 ? 'persona' : 'persone'} in ${anteprima.aziende_raggiunte} ${anteprima.aziende_raggiunte === 1 ? 'azienda' : 'aziende'}`}
          </Typography>
          <Typography variant="caption" sx={{ color: tokens.color.mist, display: 'block', mt: 0.25 }}>
            su {anteprima.utenti_valutati} persone attive e {anteprima.aziende_totali} aziende in
            piattaforma
            {anteprima.utenti_non_operativi > 0 && (
              <> · {anteprima.utenti_non_operativi} account disabilitati o sospesi esclusi</>
            )}
          </Typography>

          {nessuno && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              Controlla i ruoli scelti e le aziende incluse: con queste impostazioni nessun
              utente attivo vede il contenuto.
            </Alert>
          )}

          {righeDettaglio.length > 0 && (
            <>
              <Button
                size="small"
                onClick={() => setDettaglioAperto((v) => !v)}
                endIcon={dettaglioAperto ? <ExpandLess /> : <ExpandMore />}
                sx={{ mt: 1, minHeight: TOUCH_MIN, touchAction: 'manipulation' }}
              >
                {dettaglioAperto ? 'Nascondi il dettaglio per azienda' : 'Vedi il dettaglio per azienda'}
              </Button>
              <Collapse in={dettaglioAperto} unmountOnExit>
                <Stack spacing={0.75} sx={{ mt: 1 }}>
                  {righeDettaglio.map((r) => (
                    <Stack
                      key={r.tenantId}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ minHeight: 32 }}
                    >
                      <Typography variant="body2" noWrap sx={{ color: tokens.color.graphite }}>
                        {r.nome}
                      </Typography>
                      <SoftChip
                        label={`${r.persone} ${r.persone === 1 ? 'persona' : 'persone'}`}
                        tone="info"
                      />
                    </Stack>
                  ))}
                </Stack>
              </Collapse>
            </>
          )}

          <Typography variant="caption" sx={{ color: tokens.color.mist, display: 'block', mt: 1.25 }}>
            Conteggio calcolato come se il contenuto fosse già pubblicato. Chi collabora con più
            aziende è contato una volta sola, sulla propria azienda principale. Le funzioni vendute per singolo posto sono valutate sullo stato
            dell&apos;azienda: è un&apos;anteprima, non un registro di consegne.
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
};
